import {useState} from 'react';
import {client} from '../../../../api/client';
import {classifyAuthError} from '../../../lib/errors';
import type {FieldErrors} from '../../../types/domain';
import {FieldError} from '../../ui/FieldError';

interface Props {
    embedded?: boolean;
    onAuthSuccess: (
        token: string,
        name: string,
    ) => Promise<void>;
}

function inputClass(hasError?: string): string {
    return `w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:outline-none text-gray-900 transition-colors ${
        hasError
            ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
            : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
    }`;
}

export function ProfileAuth({onAuthSuccess, embedded = false}: Props) {
    const [isLogin, setIsLogin] = useState(true);
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

    const clearFieldError = (field: keyof FieldErrors) =>
        setFieldErrors((prev) => ({...prev, [field]: undefined}));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFieldErrors({});

        try {
            setIsSubmitting(true);
            const response = isLogin
                ? await client.account.logIn({login, password})
                : await client.account.register({login, password, name});

            await onAuthSuccess(
                response.token,
                response.user.name,
            );
        } catch (error) {
            setFieldErrors(classifyAuthError(error));
        } finally {
            setIsSubmitting(false);
        }
    };

    const switchMode = () => {
        setIsLogin((prev) => !prev);
        setFieldErrors({});
    };

    return (
        <div className={embedded ? '' : 'bg-white/80 backdrop-blur-md rounded-3xl p-8 shadow-xl'}>
            <h2 className="text-3xl font-bold mb-6 text-center bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {isLogin ? 'Вход' : 'Регистрация'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-700">
                            Имя
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                clearFieldError('name');
                            }}
                            placeholder="Введите ваше имя"
                            className={inputClass(fieldErrors.name)}
                            required
                        />
                        <FieldError message={fieldErrors.name ?? ''}/>
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                        Логин
                    </label>
                    <input
                        type="text"
                        value={login}
                        onChange={(e) => {
                            setLogin(e.target.value);
                            clearFieldError('login');
                        }}
                        placeholder="Введите логин"
                        className={inputClass(fieldErrors.login)}
                        required
                    />
                    <FieldError message={fieldErrors.login ?? ''}/>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                        Пароль
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => {
                            setPassword(e.target.value);
                            clearFieldError('password');
                        }}
                        placeholder="••••••••"
                        className={inputClass(fieldErrors.password)}
                        required
                    />
                    <FieldError message={fieldErrors.password ?? ''}/>
                </div>

                {fieldErrors.general && (
                    <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">
                        {fieldErrors.general}
                    </p>
                )}

                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting
                        ? 'Подождите...'
                        : isLogin
                            ? 'Войти'
                            : 'Зарегистрироваться'}
                </button>
            </form>

            <div className="mt-6 text-center">
                <button
                    onClick={switchMode}
                    className="text-purple-600 hover:underline text-sm"
                >
                    {isLogin
                        ? 'Нет аккаунта? Зарегистрируйтесь'
                        : 'Уже есть аккаунт? Войдите'}
                </button>
            </div>

        </div>
    );
}
